import { MouseEvent } from 'react';
import { Box, Card, CardContent, CardHeader, Divider, Typography } from '@mui/material';
import * as styles from '@pages/QuoteDetails/sidebarItems/notes/noteMessageForm/styles';
import { ToggleFilterEditor } from '@components/toggle/ToggleFilterEditor';
import { Suspense, lazy } from 'react';
import type { IRichTextEditor } from '@components/richTextEditor/RichTextEditor';
import { TextClickable, ITextClickable } from '@components/container/TextClickable';
import { NoteType } from '@models/note';
import { containerEditor } from '@components/richTextEditor/RichTextEditor.styles';
import { DerivedScopeState } from '@components/notesDialog/types';

const LazyRichTextEditor = lazy(() =>
  import('@components/richTextEditor/RichTextEditor').then((m) => ({ default: m.RichTextEditor })),
);

import { SearchField } from '@components/search';
import { PinDrawer } from '@components/drawerItem/DrawerHeader';
import * as DrawerListItems from '@pages/QuoteDetails/constants/drawerListItems';

interface ICardForm extends ICardMessageForm, ICardNoteForm {
  type: NoteType;
  hideIncludedDocumentsSection?: boolean;
  draggedFiles: File[];
  resetDraggedFiles: () => void;
  onDialogOpenChange?: (show: boolean) => void;
  isDisabled?: boolean;
  opts?: IRichTextEditor['opts'];
  sxProps?: {
    editorContainer?: React.CSSProperties;
  };
  scope?: DerivedScopeState;
}

interface ICardMessageForm {
  textClickable?: ITextClickable;
}

interface ICardNoteForm {
  selectedToggle?: string;
  onChangeToggle?: (event: MouseEvent<HTMLElement>, newValue: string) => void;
  onChangeSearchbar?: (eventValue: string) => void;
  onCleanSearchbar?: () => void;
  searchbarText?: string;
  handleScrollMessagesToBottom?: () => void;
}

export const CardForm = (props: ICardForm) => {
  const {
    type,
    hideIncludedDocumentsSection,
    draggedFiles,
    resetDraggedFiles,
    onDialogOpenChange,
    selectedToggle,
    onChangeToggle,
    searchbarText,
    onChangeSearchbar,
    onCleanSearchbar,
    textClickable,
    isDisabled = false,
    handleScrollMessagesToBottom,
    opts,
    sxProps,
    scope,
  } = props;

  return (
    <Card elevation={0} sx={styles.containerCard}>
      {textClickable ? (
        <CardHeader
          sx={{ marginTop: '8px' }}
          disableTypography
          title={
            <TextClickable
              text={textClickable.text}
              variant={textClickable.variant}
              buttonText={textClickable.buttonText}
              onClick={textClickable.onClick}
            />
          }
        />
      ) : null}
      {type === NoteType.Note && (
        <CardHeader
          title={
            <Box>
              <Typography variant='h6'>Notes</Typography>
            </Box>
          }
          action={
            <Box sx={{ display: 'flex', marginRight: '-4px' }}>
              <ToggleFilterEditor value={selectedToggle} onChange={onChangeToggle} />
              <PinDrawer urlParam={DrawerListItems.QuoteDrawerUrlParams.Notes} absolute={false} />
            </Box>
          }
        />
      )}
      {selectedToggle && selectedToggle === 'toggleFilter' ? (
        <CardContent>
          <Divider sx={styles.headerDivider} />
          <Box sx={styles.containerFilter}>
            <SearchField
              autofocus
              onChange={(e) => onChangeSearchbar?.(e)}
              onPressEndIcon={searchbarText?.length ? onCleanSearchbar : undefined}
              variant='standard'
            />
          </Box>
        </CardContent>
      ) : (
        <CardContent sx={{ paddingTop: type === NoteType.Message ? '8px' : '' }}>
          {type === NoteType.Note && <Divider sx={styles.headerDivider} />}
          <Box
            sx={containerEditor}
            style={{
              padding: type === NoteType.Message ? '0px 16px 16px 16px' : '0px 16px 0px 16px',
              ...sxProps?.editorContainer,
            }}
          >
            <Suspense fallback={null}>
              <LazyRichTextEditor
                type={type}
                hideIncludedDocumentsSection={hideIncludedDocumentsSection}
                draggedFiles={draggedFiles}
                resetDraggedFiles={resetDraggedFiles}
                onDialogOpenChange={onDialogOpenChange}
                isDisabled={isDisabled}
                handleScrollMessagesToBottom={handleScrollMessagesToBottom}
                opts={opts}
                editConfig={
                  scope
                    ? {
                        quoteId: scope.params.quoteId || undefined,
                        customerId: scope.params.customerId || undefined,
                        brokerageId: scope.params.brokerageId || undefined,
                        policyId: scope.params.policyId || undefined,
                      }
                    : undefined
                }
              />
            </Suspense>
          </Box>
        </CardContent>
      )}
    </Card>
  );
};
